<template>
  <div v-if="errorResponse">
    <WarningPanel
      :msg="errorResponse"
      :severe="true"
      :title="'There\'s a problem...'"
    />
  </div>
  <div v-else class="page-wrapper-new">
    <div
      v-if="agent.isValid"
      class="agent-message agent-summary"
      v-html="agent.message"
    />
    <!--  Package summary  -->
    <PackageSummaryNew
      :hide-unwanted-products="!agent.isValid"
      :inline-editing="agent.isValid"
      :on-toggle-products="handleToggleProducts"
      @reapply-discount="applyDiscountCode"
      class="package-summary"
      thin
    >
      <template #summary-header>
        <div class="back-button-wrapper">
          <BackButton class="back-link" />
        </div>
        <!--price per person -->
        <div
          :class="{ 'has-agent': agent.isValid }"
          class="package-summary-header"
        >
          <div class="your-package" data-testid="page-title">Your price is</div>
          <div v-show="!isDiscountValidating" id="price-discount-section">
            <section class="price-display">
              <div
                v-if="discountApplied"
                class="nondiscounted-price"
                data-testid="nondiscounted-price"
              >
                £{{ nonDiscountedPricePerPersonPerWeek }}
              </div>
              <div
                :class="discountApplied ? 'price__discount-applied' : 'price'"
                data-testid="price-discount-applied"
              >
                £{{ pricePerPersonPerWeek }}
              </div>
            </section>
            <div
              :class="{
                'mb-30': !shouldShowVouchers,
              }"
              class="pppw"
            >
              <span>Per person per week based on a 12 month contract.</span>
              <monthly-price-info
                :formatted-price-per-month="parseFloat(formattedPricePerMonth)"
              />
            </div>
            <!--        Voucher -->
            <discount-activation
              v-if="shouldShowVouchers"
              v-model.trim="discountCode"
              :summary-control="false"
              @apply="applyDiscountCode"
              @clear="clearDiscountCode"
            />
          </div>
          <div
            v-if="isDiscountValidating"
            id="price-discount-section"
            style="padding-top: 11rem; padding-bottom: 11rem;"
          >
            <i
              class="fa-solid fa-spinner-third fa-spin fa-2x"
              data-testid="voucher-loading-animation"
            ></i>
          </div>
        </div>

        <hr class="top-divider" />

        <!--    continue btn -->
        <button
          class="btn btn-summary-next btn-hide"
          data-testid="summary-next-button"
          @click="join"
        >
          Continue with this price
        </button>
        <!--    Property information -->
        <div class="property-info">
          <hr class="property-info__divider" />
          <h3
            class="package-summary-header package-summary-header--property-info"
          >
            Property information
          </h3>
          <div class="property-info-data">
            <!-- Housemates -->
            <div class="property-info__item property-info__item--people">
              <i class="fa-solid fa-user property-info-icon"></i>
              {{ housemateCount }} people
            </div>
            <!-- Address -->
            <div class="property-info__item address-or-postcode">
              <i class="fa-solid fa-house-building"></i>
              {{ addressOrPostcode }}
            </div>
          </div>
        </div>
      </template>
    </PackageSummaryNew>

    <!--    Footer buttons-->
    <div class="summary-footer-button">
      <button
        class="btn btn-summary-next"
        @click="join"
        ref="nextButtonWrapper"
      >
        Continue with this price
      </button>
      <button
        class="--save-cta"
        data-testid="package-share-button"
        @click="showSaveShareModal"
      >
        <i class="fa-regular fa-floppy-disk" style="color: #0E9F6E;"></i>
        Save or share this price
      </button>
    </div>

    <div
      class="continue-price__wrapper continue-price__wrapper--shadow continue-price__wrapper--sticky"
      ref="nextButtonWrapperSticky"
      v-show="isSticky"
    >
      <div class="continue-price__inner">
        <p style="text-align: center; font-size: 14px; margin: 16px 0 10px 0;">
          £{{ pricePerPersonPerWeek }} per person per week
        </p>
        <button
          class="btn btn-summary-next"
          style="margin-bottom: 0;"
          @click="join"
        >
          Continue with this price
        </button>
      </div>
    </div>

    <PackageSaveShareOverlay
      v-if="saveShareOverlayVisible"
      @close-panel="toggleSaveSharePanel"
    />
    <!--    Why STB footer -->
    <div class="stb-footer-info">
      <h3 class="package-summary-header" style="text-align: left">
        Why Split the Bills?
      </h3>
      <span class="stb-info">
        <i class="fa-solid fa-circle-check circle-chk"></i> Everything is
        bundled into one payment</span
      >
      <span class="stb-info">
        <i class="fa-solid fa-circle-check circle-chk"></i> Everyone pays their
        fair share</span
      >
      <span class="stb-info">
        <i class="fa-solid fa-circle-check circle-chk"></i> No hidden costs or
        fees</span
      >
    </div>
  </div>
</template>
<script>
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import PackageSummary from "@/components/PackageSummary";
import WarningPanel from "@/components/WarningPanel";
import CheckList from "@/components/CheckList";
import DiscountActivation from "@/components/DiscountActivation.vue";

import {
  initialDiscountState,
  initialPackagePrice,
} from "@/store/initialState";
import MonthlyPriceInfo from "@/components/MonthlyPriceInfo.vue";
import ShortSummaryControl from "@/components/ShortSummaryControl.vue";
import PackageSummaryNew from "@/components/PackageSummaryNew.vue";
import PackageSaveShareOverlay from "@/components/PackageSaveShareOverlay.vue";
import { findDiscountCode } from "@/utility/discount";
import TrustpilotWidget from "@/components/TrustpilotWidget.vue";

export default {
  name: "ShortSummaryTest",
  data() {
    return {
      saveShareOverlayVisible: false,
      errorResponse: "",
      isSticky: true,
    };
  },
  computed: {
    ...mapState(["agent", "discount"]),
    ...mapGetters({
      housemateCount: "housemateCount",
      postcode: "postcode",
      hasUsedDiscount: "hasUsedDiscount",
      hasAddress: "hasAddress",
      addressLine1: "addressLine1",
      address: "getCustomerAddressString",
      shouldShowVouchers: "shouldShowVouchers",
    }),
    nonDiscountedPricePerPersonPerWeek() {
      return (this.$store.getters.nonDiscountedPricePerPerson / 100).toFixed(2);
    },
    pricePerPersonPerWeek() {
      return (this.$store.getters.pricePerPerson / 100).toFixed(2);
    },
    formattedPricePerMonth() {
      return this.$store.getters.formattedPricePerMonth;
    },
    addressOrPostcode() {
      if (this.hasAddress) {
        return this.address;
      } else {
        return this.postcode;
      }
    },
    discountCode: {
      get() {
        return this.$store.state.discount.code;
      },
      set(value) {
        this.$store.commit("SET_DISCOUNT_STATE", { code: value });
      },
    },
    discountApplied: {
      get() {
        return this.$store.state.discount.isValid;
      },
      set(value) {
        this.$store.commit("SET_DISCOUNT_STATE", { isValid: value });
      },
    },
    isDiscountValidating: {
      get() {
        return this.$store.state.discount.isValidating;
      },
      set(value) {
        this.$store.commit("SET_DISCOUNT_STATE", { isValidating: value });
      },
    },
  },
  methods: {
    ...mapActions([
      "getProducts",
      "getReferralMessage",
      "getAffiliateMessage",
      "updatePreSignupLeadProductData",
      "activateDiscount",
    ]),
    ...mapMutations({
      showLoadingPanel: "SHOW_LOADING_PANEL",
      hideLoadingPanel: "HIDE_LOADING_PANEL",
      setProductOption: "SET_PRODUCT_OPTION",
      setPostcode: "SET_POSTCODE",
      setHousemateCount: "SET_HOUSEMATE_COUNT",
      setProducts: "SET_PRODUCTS",
      setPackagePrice: "SET_PACKAGE_PRICE",
      setIsViaShareLink: "IS_VIA_SHARELINK",
      hideBackButton: "HIDE_BACK_BTN",
      setDiscountState: "SET_DISCOUNT_STATE",
      setLeadTenantAddress: "SET_LEAD_TENANT_ADDRESS",
    }),
    handleToggleProducts() {
      this.$nextTick(() => {
        if (this.discountApplied) {
          this.applyDiscountCode();
        }
      });
    },
    showSaveShareModal() {
      this.trackGoogleEvent("Click Share", "Get Price", "Share price");
      this.trackFacebookEvent("Click Share");
      this.toggleSaveSharePanel();
    },
    toggleSaveSharePanel() {
      this.saveShareOverlayVisible = !this.saveShareOverlayVisible;
    },
    join() {
      this.trackGoogleEvent(
        "Click Create Account",
        "Create Account",
        "Create Account",
      );
      this.trackFacebookEvent("Click Create Account");

      if (this.hasAddress) {
        this.$router.push("/your-details");
      } else {
        this.$router.push("/your-address");
      }
    },
    applyDiscountCode() {
      this.$set(this, "isDiscountValidating", true);

      this.activateDiscount({
        voucherCode: this.$store.state.discount.code,
        selectedProducts: this.$store.getters.selectedProductPriceIds,
        housemateCount: this.$store.state.housemateCount,
        leadId: this.$store.state.leadId,
      })
        .then(({ data }) => {
          this.$set(this, "discountApplied", true);

          this.$store.commit("SET_PACKAGE_PRICE", {
            fullPriceAmount: data.priceBeforeDiscount,
            discountedPriceAmount: data.priceAfterDiscount,
          });

          this.$store.commit("SET_DISCOUNT_STATE", {
            description: data.message,
            errorMsg: "",
          });

          this.trackGoogleEvent(
            "Voucher applied successfully",
            "Voucher Apply",
            "Add Voucher",
          );
        })
        .catch((err) => {
          this.$store.commit("SET_PACKAGE_PRICE", initialPackagePrice());
          this.$store.commit("SET_DISCOUNT_STATE", {
            errorMsg:
              err.response.data.message ||
              "Invalid voucher code. Check and try again.",
          });

          this.trackGoogleEvent(
            `Voucher applied failed`,
            "Voucher Apply",
            "Add Voucher",
          );
        })
        .finally(() => {
          this.$set(this, "isDiscountValidating", false);
        });
    },
    clearDiscountCode() {
      this.$store.commit("SET_PACKAGE_PRICE", initialPackagePrice());
      this.$store.commit("SET_DISCOUNT_STATE", initialDiscountState());
    },
    loadShareLinkData() {
      const addressGuid = this.$route.query.address;

      this.getProducts({
        addressGuid,
        postcode: this.$route.query.postcode,
        housemateCount: this.$route.query.housemates,
        voucherCode: this.$route.query.voucherCode,
        hash: this.$route.query.hash,
      })
        .then(({ data }) => {
          // ensure errorResponse is reset
          this.errorResponse = "";

          if (data.address) {
            this.setLeadTenantAddress(data.address);
            this.setPostcode(data.address.postcode);
          }

          if (data.existingAccountGUID) {
            this.$router.push({
              name: "account-already-exists",
              params: { guid: data.existingAccountGUID },
            });
          } else {
            // set the store state
            if (this.$route.query.postcode) {
              this.setPostcode(this.$route.query.postcode);
            }
            this.setHousemateCount(this.$route.query.housemates);
            this.setProducts(data.products);

            if (data.priceBeforeDiscount) {
              this.nonDiscountedPricePerPersonPerWeekFormatted = (
                data.priceBeforeDiscount / 100
              ).toFixed(2);
            }

            if (data.priceAfterDiscount) {
              this.pricePerPersonPerWeekFormatted = (
                data.priceAfterDiscount / 100
              ).toFixed(2);
            }
            // Because the app has been entered mid-journey, we need to set a variable
            // that controls what the "back/editProduct" button directs to
            this.setIsViaShareLink(true);

            // Filter the query parameters ensuring the selected products are stored in state
            Object.keys(this.$route.query).forEach((key) => {
              // This mutation performs a find on the products array based on the
              // query key, so it's fine to pass the whole query parameter set
              this.setProductOption({
                type: key,
                code: this.$route.query[key],
              });
            });

            // If referral code, commit it to the store...
            if (this.$route.query.ref) {
              this.getReferralMessage(this.$route.query.ref);
            }

            // If affiliate code, commit it to the store...
            if (this.$route.query.aff) {
              this.getAffiliateMessage(this.$route.query.aff);
            }

            if (this.$route.query.voucherCode) {
              this.setDiscountState({
                inputRevealed: true,
                code: this.$route.query.voucherCode,
              });
              this.applyDiscountCode();
            }

            if (Object.hasOwn(data, "discount")) {
              this.$store.commit("SET_DISCOUNT_STATE", {
                isValid: data.discount.isValid,
                inputRevealed: data.discount.inputRevealed,
                offer: data.discount.offer,
                aff: data.discount.aff,
                code: data.discount.code,
              });

              if (Object.hasOwn(data, "leadId")) {
                this.$store.commit("SET_LEAD_ID", data.leadId);
              }
            }
          }
        })
        .catch((error) => {
          if (!error.response) {
            this.errorResponse =
              "Network error accessing the Split The Bills API";
          } else {
            this.errorResponse = error.response.data.messages[0];
          }
        })
        .finally(() => {
          this.hideLoadingPanel();
        });
    },
    toggleStickyFooter() {
      // hide sticky button if it's below form button
      const formButton = this.$refs.nextButtonWrapper;
      const formButtonRect = formButton.getBoundingClientRect();
      const windowHeight =
        window.innerHeight || document.documentElement.clientHeight;

      // adding 16px here, same as the button's bottom padding, which makes the transition a lot smoother
      this.isSticky = !(formButtonRect.bottom + 16 < windowHeight);
    },
  },
  created() {
    this.clearDiscountCode();

    // Check if this is a share link (has required query parameters)
    const hasAddressOrPostcode =
      this.$route.query.address || this.$route.query.postcode;
    const hasHousemates = this.$route.query.housemates;

    // Only run share link logic if we have the required parameters
    if (hasAddressOrPostcode && hasHousemates) {
      this.loadShareLinkData();
    } else {
      this.hideLoadingPanel();
    }
  },
  mounted() {
    findDiscountCode(this.$route.query, this.$store);
    this.updatePreSignupLeadProductData({
      offerCode: this.$store.state.discount.offer,
      affiliateCode: this.$store.state.discount.aff,
      referralCode: this.$route.query.ref,
    });

    window.addEventListener("scroll", this.toggleStickyFooter);
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.toggleStickyFooter);
  },
  components: {
    TrustpilotWidget,
    PackageSummaryNew,
    ShortSummaryControl,
    MonthlyPriceInfo,
    DiscountActivation,
    CheckList,
    PackageSaveShareOverlay,
    PackageSummary,
    WarningPanel,
  },
};
</script>

<style lang="scss" scoped>
.page-wrapper-new {
  display: flex;
  width: 500px;
  padding: 24px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 24px;
  border-radius: 24px;
  background: #fff;
  margin-bottom: 24px;

  @include media("<large") {
    width: auto;
  }

  @include media("<medium") {
    padding: 0 16px;
    align-items: center;
    gap: 10px;
  }
}

.package-summary {
  background: $white;
  text-align: center;

  .edit-pkg-button {
    margin: 2rem 0;
    text-decoration: underline;
    font-size: 20px;
    color: $brand-green;
  }
}

.back-button-wrapper {
  @include media(">large") {
    display: none;
  }
}

.package-summary-header {
  @extend h1;
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  line-height: 30px;
  margin: 0;

  &--property-info {
    text-align: left;
    font-size: 16px;

    @include media(">large") {
      font-size: 20px;
      padding: 0 0 16px 0;
    }

    @include media("<large") {
      padding-bottom: 8px;
    }
  }

  .your-package {
    color: #eb5b2b;
    padding: 0 0 8px 0;
  }

  .price-display {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.3em;
    margin-bottom: 0.5em;
    font-size: 32px;
    font-style: normal;
    font-weight: 700;
    line-height: 32px;
    color: #23a454;
    text-align: center;
  }

  .nondiscounted-price {
    color: $brand-orange;
    text-decoration: line-through;
    font-size: $form-font-size;
    font-weight: $font-medium;
  }

  .price {
    font-size: 34px;
    font-weight: $font-medium;

    &__discount-applied {
      color: $brand-green;
    }
  }

  .nondiscounted-price + .price {
    line-height: 0.9;
    margin-bottom: 4px;
  }

  .tagline {
    color: $text;
    font-weight: 300;
  }

  .pppw span {
    color: #3b4143;
    text-align: center;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 18px;
  }

  .tagline {
    margin-top: 1rem;
    font-size: 20px;
    font-weight: $font-regular;
  }

  &.has-agent {
    padding-top: $spacing-xs;
  }
}

.--main-cta {
  @include media("<medium") {
    margin-bottom: $spacing-small;
  }
}

.summary-footer-button {
  display: flex;
  width: 500px;
  padding: 24px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 24px;

  @include media("<large") {
    width: 100%;
    padding: 0;
  }
}

.btn-summary-next {
  display: flex;
  height: 60px;
  padding: 8px 16px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  align-self: stretch;
  width: 100%;
  border-radius: 8px;
  margin: 0;
  font-size: 18px;
  line-height: 27px;
}

.btn-hide {
  @include media("<large") {
    display: none !important;
  }
}

.save-action {
  text-align: center;
}

.--save-cta {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 60px;
  padding: 4px 24px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #fff;
  cursor: pointer;
  width: 100%;

  @include media("<large") {
    margin-bottom: 40px;
  }
}

.summary-header {
  text-align: center;
  margin-bottom: 0;
  font-size: 18px;
  font-weight: $font-regular;

  .address-or-postcode {
    color: $brand-orange;
  }
}

.property-info {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-self: stretch;
  margin: 24px 0;
  order: 1;

  @include media("<large") {
    order: 2;
    margin: 0;
    padding: 24px 0;
  }

  &__item {
    color: #3b4143;
    font-size: 16px;
    font-weight: 400;
    line-height: normal;
    display: flex;
    align-items: baseline;
    gap: 8px;
    text-align: left;

    &--people {
      gap: 14px;
    }
  }
}

.property-info__divider {
  border: none;
  border-top: 1px solid #e5e7eb;
  margin-top: 8px;
  width: 100%;
  flex-basis: 100%;

  @include media(">large") {
    display: none;
  }
}

.top-divider {
  border: none;
  border-top: 1px solid #e5e7eb;
  width: 100%;
  flex-basis: 100%;
  margin: 0 0 12px 0;

  @include media(">large") {
    display: none;
  }
}

.stb-footer-info {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
}

.stb-info {
  color: #3b4143 !important;
  text-align: center;
  font-size: 16px !important;
  line-height: 24px;
}

.circle-chk {
  color: #0e9f6e;
  padding: 0 8px 0 0;
}

.property-info-data {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-self: stretch;
  align-items: flex-start;
}
</style>

<style lang="scss">
.agent-summary {
  padding-top: $spacing-small;

  @include media(">=medium") {
    margin: 0 $spacing-medium;
  }
}

.mb-30 {
  margin-bottom: 30px;
}

.pppw span {
  display: block;
  margin-bottom: 4px;
}

.pppw span:last-child {
  margin-bottom: 0; /* Remove gap after the last line */
}

.btn--big {
  width: 100%;
  height: 100%;
  margin: 0 auto 2rem auto;
  min-height: 6rem;
  display: flex;
  padding: 8px 16px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  align-self: stretch;
  border-radius: 8px;
}

.continue-price {
  &__wrapper {
    font-size: 1.2rem;

    @include media("<medium") {
      padding-bottom: 1.6rem;
      display: flex;
      flex-direction: column;
      width: 100%;
      position: absolute;
      background-color: white;
      align-items: center;

      &--shadow {
        box-shadow: 0 -7px 7px -4px #dadada;
      }

      &--sticky {
        position: fixed;
        bottom: 0;
        z-index: 900;
      }
    }
  }

  &__inner {
    width: 100%;
    padding: 0 16px;
  }
}
</style>
